import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  patchVisualAssessment,
  Report,
} from "@/services/backend/htz/report/types/report";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDatePicker,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import t from "@/lang/lang";
import {
  updateHtzReportCache,
  PatchVisualAssessmentRequest,
  useHtzReportPatchVisualAssessmentMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import { UUID } from "@bitory-gmbh/nidavellir";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  Result,
  ResultItems,
} from "@/services/backend/htz/inspection/components/inspection-result";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import { ReportAssessmentRemoveDialog } from "@/services/backend/htz/report/components/report-assessment-remove-dialog";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";

export function VisualAssessmentsTable({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });

  if (!data) return;

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Komponente")}</ETableHead>
          <ETableHead>{t("Mangel")}</ETableHead>
          <ETableHead>{t("Handlungsempfehlung")}</ETableHead>
          <ETableHead>{t("Bemerkung")}</ETableHead>
          <ETableHead>{t("Durchgeführt am")}</ETableHead>
          <ETableHead>{t("Ergebnis")}</ETableHead>
          <ETableHead />
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {data.visualAssessments.length === 0 && <TableEmptyRow colSpan={7} />}
        {data.visualAssessments.map((assessment) => (
          <Row
            key={assessment.id}
            reportId={data.id}
            assessmentId={assessment.id}
          />
        ))}
      </ETableBody>
    </ETable>
  );
}

function Row({
  reportId,
  assessmentId,
}: {
  reportId: UUID;
  assessmentId: UUID;
}) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const fieldName = joinFieldName("visualAssessments", assessmentId);
  const request: PatchVisualAssessmentRequest = {
    id: reportId,
    assessmentId,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchVisualAssessmentMutation(),
  );

  const assessment = data?.visualAssessments.find((a) => a.id === assessmentId);

  if (!data || !assessment) return;

  const disabled = isImmutable(data);
  return (
    <ETableRow key={assessment.id}>
      <ETableCell className="px-0">
        <AhuComponentSelect
          ahuDataId={data.ahuDataId!}
          componentId={assessment.componentId}
          onComponentIdChange={(componentId) => {
            set(
              { ...request, componentId },
              patchVisualAssessment(data, { ...assessment, componentId }),
            );
          }}
          fieldName={joinFieldName(fieldName, "componentId")}
          disabled={disabled}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={assessment.deficiency}
        onChange={(e) => {
          const deficiency = e.target.value;
          set(
            { ...request, deficiency },
            patchVisualAssessment(data, { ...assessment, deficiency }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "deficiency")}
      />
      <ETableCellInput
        value={assessment.recommendation}
        onChange={(e) => {
          const recommendation = e.target.value;
          set(
            { ...request, recommendation },
            patchVisualAssessment(data, { ...assessment, recommendation }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "recommendation")}
      />
      <ETableCellInput
        value={assessment.comment}
        onChange={(e) => {
          const comment = e.target.value;
          set(
            { ...request, comment },
            patchVisualAssessment(data, { ...assessment, comment }),
          );
        }}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "comment")}
      />
      <ETableCellDatePicker
        date={assessment.conductedAt}
        onDateChange={(conductedAt) =>
          set(
            { ...request, conductedAt },
            patchVisualAssessment(data, { ...assessment, conductedAt }),
          )
        }
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "conductedAt")}
      />
      <ETableCellSelect
        value={assessment.result}
        onValueChange={(result) => {
          set(
            { ...request, result: result as Result },
            patchVisualAssessment(data, {
              ...assessment,
              result: result as Result,
            }),
          );
        }}
        label={t("Ergebnis")}
        items={ResultItems}
        disabled={disabled}
        fieldName={joinFieldName(fieldName, "result")}
      />
      <ETableCell className="px-0 text-right">
        {!disabled && (
          <ReportAssessmentRemoveDialog
            reportId={reportId}
            assessmentId={assessmentId}
            title={t("Visuelle Beurteilung wirklich löschen?")}
          />
        )}
      </ETableCell>
    </ETableRow>
  );
}
